import { TextInput as NDSTextInput } from "@narmi/design_system";
import cc from "classcat";
import styles from "./EligibilityCriteriaCard.module.css";
import { type EligibilityOption, EligibilityOptionSelect } from "./EligibilityOptionSelect";

const MAX_FREEFORM_FOLLOW_UP_LENGTH = 256;

interface EligibilityCriteriaCardProps {
  index: number;
  label: string;
  followUpPlaceholder: string;
  options: EligibilityOption[];
  hasFreeformFollowUp: boolean;
  isSelected: boolean;
  selectedOptionId: string | null;
  freeformAnswer: string;
  onSelect: () => void;
  onOptionSelect: (optionId: string | null) => void;
  onFreeformAnswerChange: (value: string) => void;
}

function EligibilityCriteriaCard({
  index,
  label,
  followUpPlaceholder,
  options,
  hasFreeformFollowUp,
  isSelected,
  selectedOptionId,
  freeformAnswer,
  onSelect,
  onOptionSelect,
  onFreeformAnswerChange,
}: EligibilityCriteriaCardProps) {
  const hasOptions = options.length > 0;
  const showDropdown = isSelected && hasOptions;
  const showFreeformInput = isSelected && hasFreeformFollowUp;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };

  const handleFreeformAnswerChange = (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = typeof value === "string" ? value : value.target.value;
    onFreeformAnswerChange(nextValue.slice(0, MAX_FREEFORM_FOLLOW_UP_LENGTH));
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      className={cc([styles.card, isSelected && styles.selected])}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.header}>
        <span className={cc([styles.badge, isSelected && styles.badgeSelected])}>{index + 1}</span>
        <span className={styles.label}>{label}</span>
      </div>
      {showDropdown && (
        <div
          className={styles.dropdownContainer}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <EligibilityOptionSelect
            label={followUpPlaceholder}
            options={options}
            selectedOptionId={selectedOptionId}
            onOptionSelect={onOptionSelect}
          />
        </div>
      )}
      {showFreeformInput && (
        <div
          className={styles.followUpContainer}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <NDSTextInput
            label={followUpPlaceholder}
            placeholder={followUpPlaceholder}
            value={freeformAnswer}
            onChange={handleFreeformAnswerChange}
          />
        </div>
      )}
    </div>
  );
}

export { EligibilityCriteriaCard };
export type { EligibilityOption };
